import { useQuery } from "@apollo/client";
import { ViolationFieldNotesFragment } from "generated-graphql/graphql";
import { HARMONIZED_VIOLATION_FIELD_NOTES_STATUS } from "hooks/useHarmonizedViolations";
import { useCallback, useEffect, useState } from "react";

/** How often to check while Scout writes; a turn takes tens of seconds. */
const POLL_MS = 5_000;
const WATCH_LIMIT_MS = 10 * 60_000;

/** Polls only while a job is in flight, and stops after a limit. */
export function useFieldNotesStatus({
  tenantId,
  violationId,
  skip,
}: {
  tenantId: string;
  violationId: string;
  skip: boolean;
}): {
  generating: boolean;
  latest: ViolationFieldNotesFragment | null;
  /** `writtenAt` of notes Scout finished while this was watching, if it did. */
  justWrittenAt: string | null;
  /** Staff only: whether gathering new notes should be offered now. */
  canRegenerate: boolean;
  /** Staff only: Scout's notes came from the same information the violation has now. */
  unchangedSinceGather: boolean;
  /** Call after queuing a job: shows generating at once and starts watching. */
  watch: () => Promise<void>;
  /** Re-checks quietly, e.g. after a stop, without showing generating meanwhile. */
  refresh: () => Promise<void>;
} {
  const { data, refetch, startPolling, stopPolling } = useQuery(
    HARMONIZED_VIOLATION_FIELD_NOTES_STATUS,
    {
      variables: { tenantId, harmonizedViolationId: violationId },
      skip,
      fetchPolicy: "network-only",
    }
  );
  const status = data?.harmonizedViolationFieldNotesStatus;
  // Shown from the click until the first status answer confirms the job.
  const [requested, setRequested] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const serverGenerating = Boolean(status?.generating) && !timedOut;

  useEffect(() => {
    if (!serverGenerating) {
      stopPolling();
      return undefined;
    }
    startPolling(POLL_MS);
    const limit = setTimeout(() => setTimedOut(true), WATCH_LIMIT_MS);
    return () => {
      clearTimeout(limit);
      stopPolling();
    };
  }, [serverGenerating, startPolling, stopPolling]);

  const latest = status?.current ?? null;
  // Armed from the click too, so a job that ends before the first check still counts.
  const justWrittenAt = useJustWrittenAt(requested || serverGenerating, latest);

  const watch = useCallback(async () => {
    setTimedOut(false);
    setRequested(true);
    try {
      await refetch();
    } catch {
      // The job is queued either way; a failed check only means no spinner.
    } finally {
      setRequested(false);
    }
  }, [refetch]);

  const refresh = useCallback(async () => {
    try {
      await refetch();
    } catch {
      // The next drawer open checks again.
    }
  }, [refetch]);

  return {
    refresh,
    generating: requested || serverGenerating,
    latest,
    justWrittenAt,
    canRegenerate: Boolean(status?.canRegenerate) && !requested,
    unchangedSinceGather: Boolean(status?.unchangedSinceGather),
    watch,
  };
}

/** Derived during render, so the new notes mount already knowing to type out. */
function useJustWrittenAt(
  generating: boolean,
  latest: ViolationFieldNotesFragment | null
): string | null {
  const [justWrittenAt, setJustWrittenAt] = useState<string | null>(null);
  const [watchedFrom, setWatchedFrom] = useState<{ writtenAt: string | null } | null>(null);
  if (generating && watchedFrom === null) {
    setWatchedFrom({ writtenAt: latest?.writtenAt ?? null });
  }
  if (!generating && watchedFrom !== null) {
    setWatchedFrom(null);
    if (latest && latest.writtenAt !== watchedFrom.writtenAt && !latest.writtenBy) {
      setJustWrittenAt(latest.writtenAt);
    }
  }
  return justWrittenAt;
}
